import React from "react";

import clsx from "clsx";

import ChangeFractionDisplay from "@components/change-fraction";
import { TimeSeriesChart } from "@components/reports/charts";
import { logEvent } from "@internal/analytics";
import { type metrics } from "@north-beam/nb-common";

import { MetricHeader } from "../metric-header";

export interface TopLevelSummary {
  metrics: TopLevelSummaryMetric[];
}
export interface TopLevelSummaryMetric {
  name: string;
  currentValue: string;
  format: metrics.MetricType;
  comparison?: {
    changeFraction: number;
    isPositiveChangeGood: boolean;
  };
  timeSerieses: TimeSeries[];
  xAxisFormat: "date" | "days";
  description?: string;
  hideComparisonFromTabDisplay: boolean;
  changeFractionAreaText?: string;
}
export interface TimeSeries {
  name: string;
  points: TimeSeriesPoint[];
  comparePoints?: TimeSeriesPoint[];
  bandPoints?: [TimeSeriesPoint[], TimeSeriesPoint[]];
}
export interface TimeSeriesPoint {
  date: string;
  value: string | number;
  annotations?: string[];
}

export function ChartArea(props: {
  topLevel: TopLevelSummary;
  isLoading: boolean;
}) {
  const {
    topLevel: { metrics },
    isLoading,
  } = props;

  const [selectedIndices_, setSelectedIndices] = React.useState<number[]>([
    0, 1,
  ]);
  const selectedIndices: number[] = selectedIndices_.map((selectedIndex) =>
    Math.min(metrics.length - 1, selectedIndex),
  );

  const clickCallback = React.useCallback(
    (index: number) => {
      const metric = metrics[index];
      logEvent("Selects Top Level Metric", { Metric: metric.name });
      if (selectedIndices_.includes(index)) {
        setSelectedIndices(selectedIndices_.filter((i) => i !== index));
      } else {
        if (selectedIndices_.length <= 1) {
          setSelectedIndices([...selectedIndices_, index]);
        } else {
          setSelectedIndices([selectedIndices_[1], index]);
        }
      }
    },
    [metrics, setSelectedIndices, selectedIndices_],
  );

  const selectedMetricsArray = selectedIndices.map(
    (selectedIndex) => metrics[selectedIndex],
  );

  const Tab = ({ index }: { index: number }) => {
    const metric = metrics[index];
    const isSelected = selectedIndices.includes(index);
    const changeFractionAreaText = metric.hideComparisonFromTabDisplay
      ? metric.changeFractionAreaText
      : undefined;

    return (
      <button
        className={clsx(
          { "border-2 border-cosmic": isSelected },
          "border text-left bg-white gap-1 rounded-lg py-2 px-3 min-w-[80px] hover:bg-cosmic-4",
        )}
        onClick={() => clickCallback(index)}
      >
        <MetricHeader
          name={metric.name}
          value={metric.currentValue}
          breakOnSpace
          descriptionHTML={metric.description}
        >
          {changeFractionAreaText ? (
            <small className="text-muted">{changeFractionAreaText}</small>
          ) : metric.comparison ? (
            <ChangeFractionDisplay {...metric.comparison} />
          ) : (
            <small>&nbsp;</small>
          )}
        </MetricHeader>
      </button>
    );
  };

  return (
    <div className={clsx({ "waiting-interstitial": isLoading }, "card")}>
      <div className="p-4 flex gap-1.5 flex-wrap">
        {metrics.map((_, index) => (
          <Tab index={index} key={index} />
        ))}
      </div>
      {selectedMetricsArray.map((selectedMetric) => (
        <div className="card-body" key={selectedMetric.name}>
          <h4>{selectedMetric.name}</h4>
          <TimeSeriesChart
            format={selectedMetric.format}
            yLabel={selectedMetric.name}
            serieses={selectedMetric.timeSerieses}
            xAxisFormat={selectedMetric.xAxisFormat}
            noLegend={true}
            height={50}
          />
        </div>
      ))}
    </div>
  );
}
